/* =====================================================================
   IM8-style PDP modules — shared behavior (vanilla JS, no jQuery)
   Powers: flavor selector, subscribe & save, sticky add-to-cart.
   Modules talk to each other through one shared state object + events
   on document: 'im8:flavor', 'im8:plan'.
   Built to the "IM8 front end modules dev brief - June 2026":
   - Flavor selector updates the native [name="id"] variant.
   - Subscribe & Save: 90/30-day cards expand/collapse + one-time below;
     clicking the already-open active card adds to cart; one-time adds direct.
   - Sticky ATC: upward dropdown of subscription options (one-time excluded).
   ===================================================================== */
(function () {
  'use strict';

  // ---- shared selection state ---------------------------------------
  var state = (window.IM8 = window.IM8 || {
    variantId: null,     // selected flavor variant
    flavorName: null,
    sellingPlanId: null, // selected subscription plan (null = one-time)
    planKey: null,       // '90' | '30' | 'onetime'
  });

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  // money formatter falls back to raw if Shopify global missing
  function money(cents) {
    if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  // ---- AJAX add to cart ---------------------------------------------
  function addToCart(variantId, sellingPlanId, qty) {
    var item = { id: Number(variantId), quantity: qty || 1 };
    if (sellingPlanId) item.selling_plan = Number(sellingPlanId);
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [item] }),
    }).then(function (r) { return r.json(); });
  }

  function afterAdd() {
    if (window.IM8_PREVIEW) { console.log('[IM8] (preview) add', state.variantId, state.sellingPlanId); return; }
    // 1) Rebuy Smart Cart (this theme's cart when smart_cart_enabled)
    try {
      if (window.Rebuy && window.Rebuy.SmartCart) {
        var sc = window.Rebuy.SmartCart;
        if (typeof sc.refresh === 'function') { try { sc.refresh(); } catch (e) {} }
        if (typeof sc.show === 'function') { sc.show(); return; }
        if (typeof sc.toggle === 'function') { sc.toggle(); return; }
      }
    } catch (e) { /* fall through */ }
    // 2) Dawn native cart drawer
    var drawer = document.querySelector('cart-drawer, #CartDrawer, .cart-drawer');
    if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
    // 3) Dawn cart notification
    var notif = document.querySelector('cart-notification');
    if (notif && typeof notif.open === 'function') { notif.open(); return; }
    // 4) last resort
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    window.location.href = '/cart';
  }

  function doAdd(btn) {
    if (!state.variantId) { console.warn('[IM8] no variant selected'); }
    var vid = state.variantId || (btn && btn.getAttribute('data-fallback-variant'));
    if (!vid) return;
    if (window.IM8_PREVIEW) { afterAdd(); return; }
    if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
    addToCart(vid, state.sellingPlanId, 1)
      .then(function () { afterAdd(); })
      .catch(function (e) { console.error('[IM8] add failed', e); })
      .finally(function () { if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; } });
  }
  window.IM8.addToCart = addToCart;
  window.IM8.doAdd = doAdd;

  // ---- FLAVOR SELECTOR ----------------------------------------------
  function initFlavor(root) {
    var cards = root.querySelectorAll('.im8-fs__card');
    var label = root.querySelector('.im8-fs__selected');
    function select(card) {
      if (card.classList.contains('is-soldout')) return;
      cards.forEach(function (c) { c.classList.remove('is-active'); });
      card.classList.add('is-active');
      state.variantId = card.getAttribute('data-variant-id');
      state.flavorName = card.getAttribute('data-flavor');
      if (label) label.textContent = state.flavorName;
      // keep a native variant <select>/input in sync if the theme has one
      var native = document.querySelector('[name="id"], select[name="id"], .product-form__input [name="id"]');
      if (native) { try { native.value = state.variantId; native.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {} }
      emit('im8:flavor', { variantId: state.variantId, flavorName: state.flavorName });
    }
    cards.forEach(function (card) {
      card.addEventListener('click', function () { select(card); });
    });
    var initial = root.querySelector('.im8-fs__card.is-active') ||
                  root.querySelector('.im8-fs__card:not(.is-soldout)');
    if (initial) select(initial);
  }

  // ---- SUBSCRIBE & SAVE ---------------------------------------------
  function initSubscribe(root) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-plan]'));
    var cta = root.querySelector('.im8-cta');

    function activate(card, addNow) {
      var wasActive = card.classList.contains('is-active');
      cards.forEach(function (c) { c.classList.remove('is-active'); });
      card.classList.add('is-active');
      state.planKey = card.getAttribute('data-plan');
      var sp = card.getAttribute('data-selling-plan');
      state.sellingPlanId = sp && sp !== 'onetime' ? sp : null;
      var vid = card.getAttribute('data-variant-id');
      if (vid) state.variantId = vid; // term-specific product/variant if provided
      if (cta) {
        var ctaLabel = card.getAttribute('data-cta') || 'Add to cart';
        cta.querySelector('.im8-cta__main').textContent = ctaLabel;
      }
      emit('im8:plan', { planKey: state.planKey, sellingPlanId: state.sellingPlanId, variantId: state.variantId });
      // UX from brief: clicking the already-open active card adds to cart
      if (addNow && wasActive) doAdd(cta);
    }

    cards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('.im8-acc')) return; // don't toggle when using accordion
        // one-time row adds directly to cart (brief)
        if (card.classList.contains('im8-ss__onetime')) {
          activate(card, false); doAdd(cta); return;
        }
        activate(card, true);
      });
    });

    // future-gifts accordion(s)
    root.querySelectorAll('.im8-acc__btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        btn.closest('.im8-acc').classList.toggle('is-open');
      });
    });

    if (cta) cta.addEventListener('click', function (e) { e.stopPropagation(); doAdd(cta); });

    var def = root.querySelector('[data-plan].is-active') || cards[0];
    if (def) activate(def, false);
  }

  // ---- STICKY ATC ----------------------------------------------------
  function initSticky(root) {
    var bar = root.querySelector('.im8-sticky');
    var dd = root.querySelector('.im8-dd');
    var toggle = root.querySelector('.im8-dd__toggle');
    var opts = Array.prototype.slice.call(root.querySelectorAll('.im8-dd__opt'));
    var addBtn = root.querySelector('.im8-sticky__add');
    var perEl = root.querySelector('.im8-sticky__per');

    function setOption(opt) {
      opts.forEach(function (o) { o.classList.remove('is-active'); });
      opt.classList.add('is-active');
      var main = opt.getAttribute('data-main');
      var save = opt.getAttribute('data-save');
      var per  = opt.getAttribute('data-per');
      var sp   = opt.getAttribute('data-selling-plan');
      if (toggle) {
        toggle.querySelector('.im8-dd__main').textContent = main;
        var saveEl = toggle.querySelector('.im8-dd__save');
        if (saveEl) saveEl.textContent = save || '';
      }
      if (perEl && per) perEl.textContent = per;
      if (addBtn) {
        var price = opt.getAttribute('data-price');
        var baseLabel = addBtn.getAttribute('data-add-label') || 'Add to Cart';
        addBtn.textContent = price ? (baseLabel + ' - ' + price) : baseLabel;
      }
      state.sellingPlanId = sp && sp !== 'onetime' ? sp : null;
      state.planKey = opt.getAttribute('data-plan');
      var vid = opt.getAttribute('data-variant-id');
      if (vid) state.variantId = vid;
      if (dd) dd.classList.remove('is-open');
    }

    if (toggle) toggle.addEventListener('click', function () { dd.classList.toggle('is-open'); });
    opts.forEach(function (opt) { opt.addEventListener('click', function () { setOption(opt); }); });
    document.addEventListener('click', function (e) { if (dd && !dd.contains(e.target)) dd.classList.remove('is-open'); });
    if (addBtn) addBtn.addEventListener('click', function () { doAdd(addBtn); });

    // keep sticky bar in sync when the in-page S&S module changes plan
    document.addEventListener('im8:plan', function (e) {
      var match = opts.filter(function (o) { return o.getAttribute('data-plan') === e.detail.planKey; })[0];
      if (match) setOption(match); // onetime has no sticky option -> stays on default
    });

    // Visibility: show immediately (default) or gate behind a scroll threshold/anchor.
    if (root.getAttribute('data-im8-sticky-always') === 'false') {
      var anchor = document.querySelector('[data-im8-sticky-anchor]') ||
                   document.querySelector('.im8-ss');
      var onScroll = function () {
        var trigger = anchor ? anchor.getBoundingClientRect().bottom < 0 : window.scrollY > 600;
        if (bar) bar.classList.toggle('is-visible', trigger);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    } else {
      if (bar) bar.classList.add('is-visible');
    }

    var def = root.querySelector('.im8-dd__opt.is-active') || opts[0];
    if (def) setOption(def);
  }

  // ---- boot ----------------------------------------------------------
  function boot() {
    document.querySelectorAll('[data-im8="flavor"]').forEach(initFlavor);
    document.querySelectorAll('[data-im8="subscribe"]').forEach(initSubscribe);
    document.querySelectorAll('[data-im8="sticky"]').forEach(initSticky);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // re-init inside Shopify theme editor when a section is added/changed
  document.addEventListener('shopify:section:load', boot);
})();
